import { useEffect, useRef } from 'react';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box, Grid, Skeleton, SvgIcon, Typography, useMediaQuery } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { IconSearch, IconFileExport } from '@tabler/icons-react';
import { Button, DateTimePicker, IconButton } from '@atoms';
import {
  useCustomerId,
  useIsAreasAdmin,
  useProductsByCustomer,
  useSelectedCustomerProducts,
  useShowAreaFeature,
  useUser,
} from '@hooks';
import { SelectFetch } from '@molecules';
import { SelectAreas } from '@organisms';
import { getDatesFromTheLastMonth, trackings } from '@utils';
import { zodResolver } from '@hookform/resolvers/zod';
import searchBoxConsumptionByCommerceSchema from './SearchBoxConsumptionByCommerce.schema';
import { DATE_PICKER_PROPS } from '../constants';
import useDownloadConsumptionByCommerce from '../data/useDownloadConsumptionByCommerce';
import {
  CONSUMPTION_BY_COMMERCE_CLICK_EVENT,
  EVENT_CONSUMPTION_BY_COMMERCE_CLEAR_FILTER,
  EVENT_CONSUMPTION_BY_COMMERCE_EXPORT,
} from '../events';
import styles from '../styles';

interface SearchBoxConsumptionByCommerceProps {
  filtersOpen?: boolean;
  setFiltersOpen?: (open: boolean) => void;
  isLoading?: boolean;
  params: Record<string, string>;
  onSubmit: (values: Record<string, string | Date>) => void;
  dataLength?: number;
}

/**
 * Displays filters
 * @param {SearchBoxConsumptionByCommerceProps} props
 * @param {object} props.params
 */
const SearchBoxConsumptionByCommerce: React.FC<SearchBoxConsumptionByCommerceProps> = ({
  isLoading = false,
  params,
  onSubmit,
  dataLength,
}: SearchBoxConsumptionByCommerceProps) => {
  const { t } = useTranslation();
  const responsive = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const { customerId } = useCustomerId();
  const { data: products, isLoading: isLoadingProducts } = useProductsByCustomer({ customerId });
  const previousCompanyRef = useRef<number | null>(null);
  const { fromDate, toDate } = getDatesFromTheLastMonth();
  const { download, isLoading: isLoadingConsumption } = useDownloadConsumptionByCommerce();

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm({
    defaultValues: {
      fromDate,
      toDate,
      product: params?.product || '',
      areaId: params?.areaId || '',
    },
    resolver: zodResolver(searchBoxConsumptionByCommerceSchema),
    mode: 'onChange',
  });
  const areaId = useWatch({
    control,
    name: 'areaId',
  });

  const { showAreaFeature } = useShowAreaFeature();
  const { user } = useUser();
  const { isAreasAdmin } = useIsAreasAdmin();
  const validateArea = showAreaFeature && !isAreasAdmin ? !areaId : false;
  const disableButton = isLoading || isSubmitting || validateArea;

  useEffect(() => {
    if (!isLoadingProducts) {
      const product = products.length === 1 ? products[0] : '';
      const values = { fromDate, toDate, product, areaId: '' };
      if (previousCompanyRef.current !== customerId || (products.length === 1 && !params.product)) {
        reset(values);
        onSubmit({ product });
      }
      previousCompanyRef.current = customerId;
    }
  }, [fromDate, isLoadingProducts, onSubmit, params.product, products, reset, customerId, toDate]);

  return (
    <Box sx={styles.searchContainer}>
      <Box sx={styles.header}>
        <Typography variant='h4'>{t('CONSUMPTION:FILTERS_TITLE')}</Typography>
      </Box>
      <Box sx={styles.content(responsive)}>
        <Box sx={styles.firstLine(responsive)}>
          <Grid container spacing={2}>
            <Grid item xs={6} md={3}>
              <Controller
                name='fromDate'
                control={control}
                render={({ field }) => (
                  <DateTimePicker
                    {...field}
                    handleChange={(date: Date) => field.onChange(date)}
                    error={Boolean(errors.fromDate)}
                    inputLabel='COMMON:FROM_DATE'
                    helperText={errors.fromDate ? String(errors.fromDate?.message) : undefined}
                    {...DATE_PICKER_PROPS}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <Controller
                name='toDate'
                control={control}
                render={({ field }) => (
                  <DateTimePicker
                    {...field}
                    handleChange={(date: Date) => field.onChange(date)}
                    error={Boolean(errors.toDate)}
                    inputLabel='COMMON:TO_DATE'
                    helperText={errors.toDate ? String(errors.toDate?.message) : undefined}
                    {...DATE_PICKER_PROPS}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              {isLoadingProducts ? (
                <Skeleton height={50} sx={styles.skeleton} />
              ) : (
                <Controller
                  name='product'
                  control={control}
                  render={({ field }) => (
                    <SelectFetch
                      {...field}
                      label={'COMMON:PRODUCT'}
                      error={Boolean(errors.product)}
                      getOptions={useSelectedCustomerProducts}
                      parseOption={(value: string) => ({ text: value, value })}
                      customStyles={styles.selectFetch}
                      helperText={errors.product ? String(errors.product?.message) : undefined}
                    />
                  )}
                />
              )}
            </Grid>
            {showAreaFeature && (
              <Grid item xs={6} md={3}>
                <Controller
                  name='areaId'
                  control={control}
                  render={({ field }) => (
                    <SelectAreas
                      {...field}
                      onChange={(_event, value) => {
                        field.onChange(value.id);
                      }}
                      disabled={user?.isInternal && !customerId}
                    />
                  )}
                />
              </Grid>
            )}
          </Grid>
        </Box>
        <Box sx={styles.actionsContainer(responsive)}>
          <Button
            customStyle={styles.clearButton}
            text={t('COMMON:CLEAR_FILTER')}
            color='primary'
            variant='link'
            onClick={() => {
              trackings(
                CONSUMPTION_BY_COMMERCE_CLICK_EVENT(EVENT_CONSUMPTION_BY_COMMERCE_CLEAR_FILTER)
              );
              reset();
            }}
          />
          <IconButton
            aria-label={t('CONSUMPTION:DOWNLOAD')}
            tooltip='CONSUMPTION:DOWNLOAD'
            onClick={() => {
              trackings(CONSUMPTION_BY_COMMERCE_CLICK_EVENT(EVENT_CONSUMPTION_BY_COMMERCE_EXPORT));
              download(params);
            }}
            IconComponent={IconFileExport}
            isLoading={isLoadingConsumption}
            disabled={!dataLength}
          />
          <Button
            customStyle={styles.searchButton}
            startIcon={
              <SvgIcon>
                <IconSearch />
              </SvgIcon>
            }
            variant='contained'
            color='primary'
            type='button'
            disabled={disableButton}
            isLoading={isLoading}
            onClick={handleSubmit(onSubmit)}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default SearchBoxConsumptionByCommerce;
